import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/shared/ui/command'
import { cn } from '@/shared/lib'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Article, Service, Specialist } from '@/shared/api/strapi/requests/search'
import fuzzysort from 'fuzzysort'
import { SlugLink } from '@/shared/i18n'
import Link from 'next/link'
import { ScrollArea } from '@/shared/ui/scroll-area'

interface SearchInputProps {
  invertColors?: boolean
  locale: string
  searchData: {
    specialists: Specialist[] | null
    news: Article[] | null
    services: Service[] | null
  }
}

interface CardData {
  title: string
  type: string
  slug: string
  baseHref: string
}

function fuzzyMultiWordSearchSorted(
  query: string,
  items: CardData[],
  key: 'title' = 'title',
  threshold: number = 100,
): CardData[] {
  const words = query.trim().toLowerCase().split(/\s+/)
  if (words.length === 0) return items

  const matched = []

  for (const item of items) {
    const target = (item[key] ?? '').toLowerCase()

    let totalScore = 0
    let matchedAll = true

    for (const word of words) {
      const result = fuzzysort.single(word, target)
      if (!result || result.score > threshold) {
        matchedAll = false
        break
      }
      totalScore += result.score
    }

    if (matchedAll) {
      matched.push({ item, score: totalScore })
    }
  }

  // Сортируем по totalScore (чем меньше — тем лучше)
  matched.sort((a, b) => b.score - a.score)

  return matched.map(m => m.item)
}

export const SearchInput = ({ invertColors, locale, searchData }: SearchInputProps) => {
  const searchParams = useSearchParams()
  const queryParam = searchParams.get('text') ?? ''
  const [query, setQuery] = useState(queryParam)
  const [isFocused, setIsFocused] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const searchHref = useMemo(() => {
    const encoded = encodeURIComponent(query.trim())
    return encodeURIComponent(query.trim()) ? `/${locale}/search?text=${encoded}` : `/${locale}/search`
  }, [locale, query])
  const router = useRouter()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (ref.current && !ref.current.contains(target)) {
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const encoded = encodeURIComponent(query.trim())
        const href = encoded ? `/${locale}/search?text=${encoded}` : `/${locale}/search`
        router.push(href)
        setIsFocused(false)
      }
    },
    [locale, query, router],
  )

  const cards = useMemo((): CardData[] => {
    let res: CardData[] = []

    if (query.trim() === '') {
      return res
    }

    if (searchData.specialists) {
      res = [
        ...res,
        ...searchData.specialists.map<CardData>(specialist => ({
          title: specialist.attributes.name,
          type: 'Specialists',
          baseHref: '/specialists',
          slug: specialist.attributes.slug,
        })),
      ]
    }
    if (searchData.news) {
      res = [
        ...res,
        ...searchData.news.map<CardData>(article => ({
          title: article.attributes.title,
          type: 'News',
          baseHref: '/news',
          slug: article.attributes.slug,
        })),
      ]
    }
    if (searchData.services) {
      res = [
        ...res,
        ...searchData.services.map<CardData>(service => ({
          title: service.attributes.name,
          type: 'Services',
          baseHref: '/service',
          slug: service.attributes.slug,
        })),
      ]
    }

    // если нет текста — возвращаем всё
    if (!query.trim()) return res.sort((a, b) => a.title.localeCompare(b.title))

    return fuzzyMultiWordSearchSorted(query, res)
  }, [searchData, query])

  return (
    <div ref={ref} className="relative w-full max-w-xl">
      <Command
        shouldFilter={false}
        className={cn(
          'w-full rounded-3xl border',
          invertColors
            ? 'bg-black/10 text-black border-black/10 focus:ring-black/20'
            : 'bg-white/10 text-white border-white/20 focus:ring-white/30',
        )}
      >
        <CommandInput
          className={cn(
            'text-current placeholder:text-sm px-5 py-3',
            invertColors ? 'placeholder:text-black/40' : 'placeholder:text-white/80',
          )}
          placeholder="Search"
          value={query}
          onClear={() => setQuery('')}
          onValueChange={setQuery}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
        />

        {query.trim() && isFocused && (
          <div className={'flex flex-col'}>
            <CommandList className="max-h-[400px] overflow-y-auto text-black absolute top-full left-0 mt-2 w-full bg-white rounded-2xl z-30 ">
              <CommandEmpty>No results found</CommandEmpty>

              {cards.length > 0 && (
                <ScrollArea aria-orientation={'vertical'} className={'h-[300]'}>
                  {cards.map(card => (
                    <CommandItem
                      value={card.baseHref + card.slug}
                      key={`${card.baseHref}-${card.slug}`}
                      className="flex flex-col items-start px-4 py-2 text-black"
                      asChild
                    >
                      <SlugLink
                        baseHref={card.baseHref}
                        slug={card.slug}
                        className={'flex flex-col'}
                        onClick={() => setIsFocused(false)}
                      >
                        <span className="text-sm font-medium">{card.title}</span>
                        <span className="text-xs text-muted-foreground">{card.type}</span>
                      </SlugLink>
                    </CommandItem>
                  ))}
                </ScrollArea>
              )}
              {cards.length > 0 && (
                <CommandItem className={'border-t border-gray-400 rounded-t-none'}>
                  <Link
                    className={'w-full text-center px-4 py-2 text-black '}
                    onClick={() => setIsFocused(false)}
                    href={searchHref}
                  >
                    Show All Matches →
                  </Link>
                </CommandItem>
              )}
            </CommandList>
          </div>
        )}
      </Command>
    </div>
  )
}

// import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/shared/ui/command'
// import { cn } from '@/shared/lib'
// import { useRouter, useSearchParams } from 'next/navigation'
// import { useMemo, useState } from 'react'
// import { Article, Service, Specialist } from '@/shared/api/strapi/requests/search'
// import fuzzysort from 'fuzzysort'
//
// interface SearchInputProps {
//   invertColors?: boolean
//   locale: string
//   searchData: {
//     specialists: Specialist[] | null
//     news: Article[] | null
//     services: Service[] | null
//   }
// }
//
// interface CardData {
//   title: string
//   type: string
//   slug: string
//   baseHref: string
// }
//
// function fuzzyMultiWordSearchSorted(
//   query: string,
//   items: CardData[],
//   key: 'title' = 'title',
//   threshold: number = 100,
// ): CardData[] {
//   const words = query.trim().toLowerCase().split(/\s+/)
//   if (words.length === 0) return items
//
//   const matched = []
//
//   for (const item of items) {
//     const target = (item[key] ?? '').toLowerCase()
//
//     let totalScore = 0
//     let matchedAll = true
//
//     for (const word of words) {
//       const result = fuzzysort.single(word, target)
//       if (!result || result.score > threshold) {
//         matchedAll = false
//         break
//       }
//       totalScore += result.score
//     }
//
//     if (matchedAll) {
//       matched.push({ item, score: totalScore })
//     }
//   }
//
//   // Сортируем по totalScore (чем меньше — тем лучше)
//   matched.sort((a, b) => b.score - a.score)
//
//   return matched.map(m => m.item)
// }
//
// export const SearchInput = ({ invertColors, locale, searchData }: SearchInputProps) => {
//   const searchParams = useSearchParams()
//   const queryParam = searchParams.get('text') ?? ''
//   const [query, setQuery] = useState(queryParam)
//   const router = useRouter()
//
//   const handleSearchSubmit = (e: React.FormEvent) => {
//     e.preventDefault()
//     const encoded = encodeURIComponent(query.trim())
//     if (!encoded) {
//       router.push(/${locale}/search)
//       return
//     }
//     router.push(/${locale}/search?text=${encoded})
//   }
//
//   const cards = useMemo((): CardData[] => {
//     let res: CardData[] = []
//
//     if (query.trim() === '') {
//       return res
//     }
//
//     if (searchData.specialists) {
//       res = [
//         ...res,
//         ...searchData.specialists.map<CardData>(specialist => ({
//           title: specialist.attributes.name,
//           type: 'Specialists',
//           baseHref: '/specialists',
//           slug: specialist.attributes.slug,
//         })),
//       ]
//     }
//     if (searchData.news) {
//       res = [
//         ...res,
//         ...searchData.news.map<CardData>(article => ({
//           title: article.attributes.title,
//           type: 'News',
//           baseHref: '/news',
//           slug: article.attributes.slug,
//         })),
//       ]
//     }
//     if (searchData.services) {
//       res = [
//         ...res,
//         ...searchData.services.map<CardData>(service => ({
//           title: service.attributes.name,
//           type: 'Services',
//           baseHref: '/service',
//           slug: service.attributes.slug,
//         })),
//       ]
//     }
//
//     // если нет текста — возвращаем всё
//     if (!query.trim()) return res.sort((a, b) => a.title.localeCompare(b.title))
//
//     return fuzzyMultiWordSearchSorted(query, res)
//   }, [searchData, query])
//
//   console.log(cards)
//
//   return (
//     <Command
//       className={cn(
//         'rounded-3xl',
//         invertColors
//           ? 'bg-black/10 text-black  border-black/10 focus:ring-black/20'
//           : 'bg-white/10 text-white  border-white/20 focus:ring-white/30',
//       )}
//     >
//       <CommandInput
//         className={cn('text-current', invertColors ? 'placeholder:text-black/40' : 'placeholder:text-white/80')}
//         placeholder={'Search'}
//         value={query}
//         onValueChange={setQuery}
//         onDragEnter={handleSearchSubmit}
//       />
//       <CommandList>
//         {cards.length === 0 && <CommandEmpty>not found</CommandEmpty>}
//         {cards.map(card => (
//           <CommandItem key={card.baseHref + card.slug}>{card.title}</CommandItem>
//         ))}
//       </CommandList>
//       {/*<input*/}
//       {/*  type="text"*/}
//       {/*  placeholder="Search"*/}
//       {/*  value={query}*/}
//       {/*  onChange={e => setQuery(e.target.value)}*/}
//       {/*  className={w-full py-2 pl-5 pr-12 rounded-full backdrop-blur-sm border focus:outline-none focus:ring-2*/}
//       {/*          ${*/}
//       {/*            !shouldInvertColors || invertColors*/}
//       {/*              ? 'bg-black/10 text-black placeholder-black/40 border-black/10 focus:ring-black/20'*/}
//       {/*              : 'bg-white/10 text-white placeholder-white/80 border-white/20 focus:ring-white/30'*/}
//       {/*          }*/}
//       {/*          }*/}
//       {/*/>*/}
//     </Command>
//   )
// }
